import {
  type FocusEvent,
  type PropsWithChildren,
  useEffect,
  useRef,
  useState
} from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Icon } from '@elfsight/lib-icon';
import { COMMON_MOBILE_BREAKPOINT } from '@elfsight/lib-utils';
import { useIsMobile } from '@elfsight/widget-utils';
import type { Mode, ModeData } from '../../types';
import { useA11yState } from '../hooks/use-a11y-state';
import { ModeInfoIcon } from './mode-info-icon';

type ModeItemProps = ModeData &
  PropsWithChildren<{
    modeName: Mode;
  }>;

export function ModeItem({
  label,
  description,
  icon,
  modeName,
  settings
}: ModeItemProps) {
  const theme = useTheme();
  const { a11yState, setA11yMode, resetA11yState } = useA11yState();
  const [active, setActive] = useState(false);
  const isMobile = useIsMobile();
  const infoIconRef = useRef<HTMLDivElement | null>(null);

  const handleClick = () => {
    setActive((prev) => !prev);

    if (active) {
      resetA11yState();
      return;
    }

    setA11yMode(modeName, settings);
  };

  const handleContainerFocus = (event: FocusEvent<HTMLDivElement>) => {
    const isFocusFromOtherElement = event.relatedTarget;

    if (!isMobile && infoIconRef.current && isFocusFromOtherElement) {
      infoIconRef.current.focus();
    }
  };

  const handleContainerBlur = () => {
    if (!isMobile && infoIconRef.current) {
      infoIconRef.current.blur();
    }
  };

  useEffect(() => {
    const mode = a11yState?.mode;

    setActive(mode === modeName);
  }, [a11yState, modeName]);

  return (
    <Container
      $active={active}
      role="radio"
      aria-checked={active}
      aria-label={label}
      onClick={handleClick}
      onFocus={handleContainerFocus}
      onBlur={handleContainerBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      <Icon
        component={icon}
        size={24}
        fill={
          active ? theme.modeItem.activeIconColor : theme.modeItem.iconColor
        }
      />
      <Label $active={active}>{label}</Label>

      <InfoIcon
        ref={infoIconRef}
        label={label}
        description={description}
        active={active}
      />
    </Container>
  );
}

const InfoIcon = styled(ModeInfoIcon)``;

const Container = styled.div<{
  $active: boolean;
}>`
  ${({ theme, $active }) => css`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 15px;
    border-radius: 100px;
    cursor: pointer;
    transition-property: background-color, opacity;
    transition-duration: 0.15s;
    background-color: ${$active
      ? theme.modeItem.activeBackgroundColor
      : theme.modeItem.backgroundColor};

    &:hover {
      background-color: ${$active
        ? theme.modeItem.activeHoverBackgroundColor
        : theme.modeItem.hoverBackgroundColor};

      & > ${InfoIcon} {
        opacity: 1;
      }
    }

    &:has(${InfoIcon}:focus-visible) {
      background-color: rgb(
        from ${theme.modeInfoIcon.color} r g b / ${$active ? 90 : 10}%
      );
      outline: 3px solid ${theme.modeItem.outlineColor};
      outline-offset: 2px;
      z-index: 1;

      & > ${InfoIcon} {
        opacity: 1;
      }
    }

    @media (max-width: ${COMMON_MOBILE_BREAKPOINT}px) {
      background-color: ${$active
        ? theme.modeItem.mobile.activeBackgroundColor
        : theme.modeItem.mobile.backgroundColor};
      padding: 15px;
      box-shadow:
        0px 2px 6px 0px rgba(0, 0, 0, 0.05),
        0px 5px 15px 0px rgba(0, 0, 0, 0.05);

      &:hover {
        background-color: ${$active
          ? theme.modeItem.mobile.activeHoverBackgroundColor
          : theme.modeItem.mobile.hoverBackgroundColor};
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

const Label = styled.div<{ $active: boolean }>`
  ${({ theme, $active }) => css`
    color: ${$active
      ? theme.modeItem.activeLabelColor
      : theme.modeItem.labelColor};
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    flex-grow: 1;
  `}
`;
